"use client"

import * as React from "react"
import { useRouter, useSearchParams, useParams } from "next/navigation"
import Link from "next/link"
import { ChevronLeft, Moon, Sun, Loader2 } from "lucide-react"
import { PlanFilters } from "@/components/plan/PlanFilters"
import { PlanComparisonCard } from "@/components/plan/PlanComparisonCard"
import { CompareToast } from "@/components/plan/CompareToast"
import { CompareTray } from "@/components/plan/CompareTray"
import {
  SMECategoryTabs,
  type SMECategory,
} from "@/components/plan/SMECategoryTabs"
import { QuoteConfirmModal } from "@/components/plan/QuoteConfirmModal"
import { AuthModal } from "@/components/auth/AuthModal"
import { NovaLogo } from "@/public/images/NovaLogo"
import { SalamaLogo } from "@/public/images/SalamaLogo"
import { useAuthStore } from "@/hooks/use-auth"
import { getCookie } from "@/utils/cookies"
import {
  COOKIE_NAME,
  useVisitorQuotesStore,
  type GeneratedQuote,
} from "@/stores/visitor-quotes"
import { Button } from "@/components/ui/button"
import { ROUTES } from "@/lib/routes"
import { useAuthModalStore } from "@/hooks/use-auth-modal"
import type { PlanDisplayData, PlanCategory } from "@/lib/plan-display-data"
import { getOpNetworkLabel, getIpNetworkLabel } from "@/lib/op-network-labels"
import { resolveSelectedRoomTypeLabel } from "@/lib/room-type-display"
import { resolveSelectedAnnualMedicalLimit } from "@/lib/category-plan-meta"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"

function mapQuoteToPlan(q: GeneratedQuote): PlanDisplayData {
  const category: PlanCategory = q.product.type === "sme" ? "sme" : "individual"
  // Build benefits map from optionalBenefits
  const benefits: Record<string, string> = {}
  q.optionalBenefits?.forEach((ob) => {
    const selectedOpt =
      ob.options?.find((o) => o.id === ob.selectedId) ??
      ob.options?.find((o) => o.isDefault) ??
      ob.options?.[0]
    if (selectedOpt) {
      const limit = selectedOpt.limitValue ?? ""
      const copay = selectedOpt.copayPct ? `${selectedOpt.copayPct}%` : ""
      benefits[ob.benefitType] = limit || copay || "Included"
    } else {
      benefits[ob.benefitType] = "—"
    }
  })
  return {
    slug: q.id,
    name: q.planTier.name,
    productName: q.product.name,
    category,
    badge: null,
    claimSettlementRatio: 0,
    lifeCover: "",
    policyTerm: 1,
    yearlyPrice: q.totalWithVat,
    monthlyPrice: Math.round((q.totalWithVat / 12) * 100) / 100,
    features: [],
    includedRiders: 0,
    optionalRiders: q.optionalBenefits?.length ?? 0,
    trustBadges: [],
    tpa: q.ratesSnapshot?.tpa_selected ?? q.product.defaultTpa ?? "",
    annualMedicalLimit:
      resolveSelectedAnnualMedicalLimit({
        planTier: q.planTier,
        benefitSelections: q.benefitSelections,
        adjustmentFactors: q.adjustmentFactors,
        optionalBenefitCatalog: q.optionalBenefits,
        currency: q.currency ?? q.product.currency ?? "AED",
      }) ||
      q.planTier.annualLimit ||
      "",
    coverageArea:
      q.product.type === "micro_sme"
        ? ((q.planTier.staticDetails?.territory as string | undefined) ?? "")
        : (q.product.emirate ?? ""),
    networkHospitals: 0,
    networkClinics: 0,
    roomType:
      resolveSelectedRoomTypeLabel({
        adjustmentFactors: q.adjustmentFactors,
        optionalBenefits: q.optionalBenefits,
      }) ??
      q.planTier.roomType ??
      "",
    productCode: q.product.code,
    productId: q.product.id,
    planTierId: q.planTier.id,
    benefitCategory: q.benefitCategory,
    ratesSnapshot: q.ratesSnapshot,
    visaEmirate: q.pricingMembers?.[0]?.visaEmirate ?? undefined,
    selectedOpNetwork: resolveSelectedOpNetwork(
      q.product.type,
      (
        q.ratesSnapshot as {
          pricing_type?: string | null
          pricingType?: string | null
        } | null
      )?.pricing_type ??
        (
          q.ratesSnapshot as {
            pricing_type?: string | null
            pricingType?: string | null
          } | null
        )?.pricingType,
      q.planTier.networkOp,
      q.planTier.name,
      q.ratesSnapshot?.tpa_selected ?? q.product.defaultTpa
    ),
    selectedIpNetwork: resolveSelectedIpNetwork(
      q.planTier.name,
      q.ratesSnapshot?.tpa_selected ?? q.product.defaultTpa,
      q.planTier.networkIp
    ),
    benefits,
    benefitSelections: q.benefitSelections?.benefitSelections,
    planTier: q.planTier,
    adjustmentFactors: q.adjustmentFactors?.map((af) => ({
      factorType: af.factorType,
      required: af.required,
      selectedId: af.selectedId,
      options:
        af.options?.map((opt) => ({
          id: opt.id,
          optionLabel: opt.optionLabel,
          multiplier: opt.multiplier,
          isDefault: opt.isDefault,
        })) ?? [],
    })),
    // Surfaces the persisted selectedId per benefit_type so the Configure
    // Benefits modal hydrates from saved state (otherwise it falls back to
    // "cheapest row" defaults and silently overwrites the user's pick on
    // the next save).
    optionalBenefits: q.optionalBenefits?.map((ob) => ({
      benefitType: ob.benefitType,
      required: ob.required,
      selectedId: ob.selectedId,
      options:
        ob.options?.map((opt) => ({
          id: opt.id,
          copayPct: opt.copayPct,
          limitValue: opt.limitValue as unknown as number | null,
          rate: opt.rate,
          isFlatRate: opt.isFlatRate,
          isDefault: opt.isDefault,
        })) ?? [],
    })),
    priceBreakdown: {
      currency: q.currency ?? "AED",
      totalNet: q.totalNet ?? 0,
      totalGross: q.totalGross ?? 0,
      totalWithVat: q.totalWithVat ?? 0,
      vat: Math.max(0, (q.totalWithVat ?? 0) - (q.totalGross ?? 0) - (q.basmah ?? 0) - (q.icp ?? 0)),
      loadings: Math.max(0, (q.totalGross ?? 0) - (q.totalNet ?? 0)),
      basmah: q.basmah ?? 0,
      basmahMemberCount: q.basmahMemberCount ?? 0,
      icp: q.icp ?? 0,
      icpMemberCount: q.icpMemberCount ?? 0,
      fees: q.fees ?? [],
      members: (
        (q.ratesSnapshot?.members as
          | Array<{
              name?: string
              relationship: string
              ageInYears?: number
              gender?: string
              visaEmirate?: string
              rate: number
            }>
          | undefined) ?? []
      ).map((m) => ({
        name: m.name,
        relationship: m.relationship,
        age: m.ageInYears,
        gender: m.gender,
        visaEmirate: m.visaEmirate,
        rate: m.rate,
      })),
      // Server pre-aggregates per-benefit totals across all members so the
      // tooltip doesn't need the heavy members JSON (stripped from the list).
      optionalBenefits: (q.optionalBenefitTotals ?? []).map((t) => ({
        benefitType: t.benefitType,
        label: t.label,
        rate: t.rate,
      })),
      pricingMembers: q.pricingMembers ?? [],
      loadingBreakdown: q.loadingBreakdown ?? [],
      fullMembers: (q.fullMembers ?? []).map((m) => ({
        name: m.name,
        dob: m.dob ?? "",
        gender: m.gender ?? "",
        relationship: m.relationship ?? "",
        age: m.age ?? 0,
        maritalStatus: m.maritalStatus ?? null,
        visaEmirate: m.visaEmirate ?? null,
        baseRate: m.baseRate ?? 0,
        adjustmentFactor: m.adjustmentFactor ?? 1,
        modifiedBase: m.modifiedBase ?? 0,
        optionalPremium: m.optionalPremium ?? 0,
        netRate: m.netRate ?? 0,
        grossPremium: m.grossPremium ?? 0,
        finalPremium: m.finalPremium ?? 0,
        memberFees: m.memberFees,
        optionalBreakdown: m.optionalBreakdown ?? [],
        adjustmentBreakdown: m.adjustmentBreakdown,
      })),
      benefitCategory: q.benefitCategory,
      planName: q.planTier?.name,
      tpa: q.ratesSnapshot?.tpa_selected ?? q.product?.defaultTpa ?? undefined,
    },
  }
}

/**
 * Resolve the selected outpatient network label for Micro SME/SME from pricing_type.
 * Returns the tier's base networkOp if pricing_type is not set or not Micro SME/SME.
 */
function resolveSelectedOpNetwork(
  productType: string | undefined,
  pricingType: string | null | undefined,
  baseNetworkOp: string | undefined,
  planName: string | undefined,
  tpa: string | null | undefined
): string | undefined {
  if (productType !== "micro_sme" && productType !== "sme") return baseNetworkOp

  // Parse pricing_type suffix to determine network level (applicable only to micro_sme)
  let networkLevel: "standard" | "full" | "alt" = "standard"
  if (productType === "micro_sme" && pricingType) {
    if (pricingType.endsWith("_upgraded_alt")) {
      networkLevel = "alt"
    } else if (pricingType.endsWith("_upgraded")) {
      networkLevel = "full"
    }
  }

  // Look up the actual network label using the mapping
  const label = getOpNetworkLabel(planName, tpa ?? undefined, networkLevel)
  return label ?? baseNetworkOp
}

/**
 * Resolve the selected inpatient network label from planName and TPA.
 */
function resolveSelectedIpNetwork(
  planName: string | undefined,
  tpa: string | null | undefined,
  baseNetworkIp?: string
): string | undefined {
  return getIpNetworkLabel(planName, tpa ?? undefined) ?? baseNetworkIp
}

function ThemeToggle({ className }: { className?: string }) {
  const [isDark, setIsDark] = React.useState(false)

  React.useEffect(() => {
    const isDarkMode = document.documentElement.classList.contains("dark")
    setIsDark(isDarkMode)
  }, [])

  const toggleTheme = () => {
    const newIsDark = !isDark
    setIsDark(newIsDark)
    if (newIsDark) {
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
  }

  return (
    <button
      onClick={toggleTheme}
      className={className}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {isDark ? <Sun size={20} /> : <Moon size={20} />}
    </button>
  )
}

export default function HomePage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { locale: localeParam } = useParams()
  const locale = (localeParam as string) ?? "en"
  const userInfoCompleted = !!getCookie(COOKIE_NAME)
  const [isChecking, setIsChecking] = React.useState(true)
  const tokens = useAuthStore((s) => s.tokens)
  const openAuthModal = useAuthModalStore((s) => s.openModal)
  const selectedPlanFromLanding = searchParams.get("selected")

  const {
    generatedQuotes,
    categories,
    quoteRequest,
    listLoading,
    listError,
    fetchGeneratedQuotesFromCookie,
    fetchCategoriesFromCookie,
    contactQuote,
    resetQuotes,
    fetchContactedSummary,
    contactedSummary,
    summaryLoading,
    isResetting,
    mergeContacted,
    mergeResult,
    isMerging,
  } = useVisitorQuotesStore()
  const isSMEQuote = quoteRequest?.request_type === "corporate"
  const isInsuredWithSalama =
    quoteRequest?.individual_detail?.is_insured_with_salama === true ||
    quoteRequest?.corporate_detail?.is_insured_with_salama === true
  const smeCategories: SMECategory[] = React.useMemo(() => {
    if (!isSMEQuote) return []
    return categories.map((key, i) => ({
      label: `Category ${String.fromCharCode(65 + i)}`,
      key,
    }))
  }, [isSMEQuote, categories])
  const [activeCategoryIndex, setActiveCategoryIndex] = React.useState(0)
  const [hintDismissed, setHintDismissed] = React.useState(false)
  const [showResetDialog, setShowResetDialog] = React.useState(false)
  const [showQuoteConfirmModal, setShowQuoteConfirmModal] =
    React.useState(false)
  const isPremiumSummaryStep =
    isSMEQuote && activeCategoryIndex === smeCategories.length

  // Drive selected plans from API contacted summary — keyed by benefit_category → planTierId (stable across resets)
  const selectedPlanPerCategory = React.useMemo<Record<string, string>>(() => {
    if (!contactedSummary) return {}
    const map: Record<string, string> = {}
    contactedSummary.groups.forEach((g) => {
      // Prefer planTier.id (stable) so it matches plan.planTierId in the card list
      const tierId = g.planTierId ?? g.planTier?.id ?? g.quoteId
      map[g.benefit_category] = tierId
    })
    return map
  }, [contactedSummary])

  const allCategoriesSelected =
    isSMEQuote &&
    smeCategories.length > 0 &&
    smeCategories.every((cat) => !!selectedPlanPerCategory[cat.key])
  const selectedCategoriesCount = smeCategories.filter(
    (cat) => !!selectedPlanPerCategory[cat.key]
  ).length
  const memberCount = React.useMemo(() => {
    if (generatedQuotes.length > 0) return generatedQuotes[0].memberCount ?? 1
    return 1
  }, [generatedQuotes])

  // Filter states
  const [lifeCover, setLifeCover] = React.useState("500000")
  const [policyTerm, setPolicyTerm] = React.useState("15")
  const [paymentFrequency, setPaymentFrequency] = React.useState<
    "annually" | "monthly"
  >("annually")
  const [planType, setPlanType] = React.useState<"all" | "individual" | "sme">(
    "all"
  )
  const [sortBy, setSortBy] = React.useState("relevance")
  const [selectedPlanSlug, setSelectedPlanSlug] = React.useState<string | null>(
    null
  )
  const apiPlans = React.useMemo(
    () => generatedQuotes.map(mapQuoteToPlan),
    [generatedQuotes]
  )

  // Track when initial data fetch is in progress - show loader until all APIs complete
  const [initialFetchInProgress, setInitialFetchInProgress] =
    React.useState(true)

  // Use the dedicated list loading/error state — isolated from isLoading which
  // covers createIndividual / createCorporate / fetchById (user-info prefill).
  // Also show loading during initial fetch to prevent "no plans available" flash
  const plansLoading = listLoading || initialFetchInProgress
  const plansError = listError

  const logout = useAuthStore((s) => s.logout)

  // Initial fetch: categories + quotes (and contact summary for SME)
  // Track completion to prevent "no plans available" flash before APIs are called
  const initialFetchStartedRef = React.useRef(false)
  React.useEffect(() => {
    if (initialFetchStartedRef.current) return
    initialFetchStartedRef.current = true

    async function doInitialFetch() {
      setInitialFetchInProgress(true)
      try {
        // Step 1: Fetch categories first
        await fetchCategoriesFromCookie()
        // Categories loaded - show UI immediately, let listLoading handle plans
        setInitialFetchInProgress(false)
        // Step 2: After categories, fetch quotes (and contact summary for SME)
        // The other useEffects will handle these
      } catch {
        setInitialFetchInProgress(false)
      }
    }
    doInitialFetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch contacted summary once categories are loaded — only for corporate quotes
  const summaryFetchStartedRef = React.useRef(false)
  React.useEffect(() => {
    if (categories.length === 0) return
    if (!isSMEQuote) return
    if (summaryFetchStartedRef.current) return
    summaryFetchStartedRef.current = true
    fetchContactedSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length, isSMEQuote])

  // Fetch quotes when categories load or category tab changes (skip on Premium Summary step)
  const lastFetchedCatKeyRef = React.useRef<string | undefined | null>(null)
  const quotesFetchStartedRef = React.useRef(false)
  React.useEffect(() => {
    if (categories.length === 0) return
    if (isPremiumSummaryStep) return
    const catKey = isSMEQuote
      ? smeCategories[activeCategoryIndex]?.key
      : undefined
    // Skip if we already fetched for this exact category key
    if (lastFetchedCatKeyRef.current === (catKey ?? "__individual__")) return
    lastFetchedCatKeyRef.current = catKey ?? "__individual__"
    quotesFetchStartedRef.current = true
    fetchGeneratedQuotesFromCookie(catKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categories.length,
    activeCategoryIndex,
    isSMEQuote,
    smeCategories,
    isPremiumSummaryStep,
  ])

  // Fetch contacted summary only once when transitioning INTO the Premium Summary step (corporate only)
  const prevIsPremiumSummaryRef = React.useRef(false)
  React.useEffect(() => {
    if (
      isPremiumSummaryStep &&
      !prevIsPremiumSummaryRef.current &&
      isSMEQuote
    ) {
      fetchContactedSummary()
    }
    prevIsPremiumSummaryRef.current = isPremiumSummaryStep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremiumSummaryStep, isSMEQuote])

  async function handleLogout() {
    const locale = window.location.pathname.split("/")[1] ?? "en"
    await logout()
    router.push(`/${locale}`)
  }

  // Calculate active filter count
  const activeFilterCount = React.useMemo(() => {
    let count = 0
    if (lifeCover !== "500000") count++
    if (policyTerm !== "15") count++
    if (paymentFrequency !== "annually") count++
    if (planType !== "all") count++
    if (sortBy !== "relevance") count++
    return count
  }, [lifeCover, policyTerm, paymentFrequency, planType, sortBy])

  // Clear all filters
  const handleClearFilters = () => {
    setLifeCover("500000")
    setPolicyTerm("15")
    setPaymentFrequency("annually")
    setPlanType("all")
    setSortBy("relevance")
  }

  // Redirect to landing website plans page if user-info not completed
  React.useEffect(() => {
    if (!userInfoCompleted) {
      router.replace(`/${locale}/user-info`)
      setIsChecking(false)
      return
    }
    if (selectedPlanFromLanding) {
      setSelectedPlanSlug(selectedPlanFromLanding)
    }
    setIsChecking(false)
  }, [userInfoCompleted, selectedPlanFromLanding, router, locale])

  // Filter and sort plans
  const filteredPlans = React.useMemo(() => {
    let plans = [...apiPlans]

    // Filter by plan type
    if (planType !== "all") {
      plans = plans.filter((p) => p.category === planType)
    }

    // Sort
    if (sortBy === "price-low") {
      plans.sort((a, b) => {
        const priceA = a.yearlyPrice || Infinity
        const priceB = b.yearlyPrice || Infinity
        return priceA - priceB
      })
    } else if (sortBy === "price-high") {
      plans.sort((a, b) => {
        const priceA = a.yearlyPrice || 0
        const priceB = b.yearlyPrice || 0
        return priceB - priceA
      })
    }

    return plans
  }, [apiPlans, planType, sortBy])

  async function handleSelectPlanForCategory(planSlug: string) {
    const catKey = smeCategories[activeCategoryIndex]?.key ?? ""
    await contactQuote(planSlug, catKey)
  }

  // Show loading state while checking
  if (isChecking) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-nova-600" />
          <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
            Checking enrollment status...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-svh flex-col bg-slate-50 dark:bg-slate-950">
      {/* Header — only shown when NOT logged in; authenticated users get DashboardTopBar from AuthenticatedLayout */}
      {!tokens && (
        <header className="border-b border-slate-200 bg-white px-6 py-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto flex max-w-5xl items-center justify-between">
            <div className="flex items-center gap-4">
              <NovaLogo className="h-10 w-auto text-slate-900 dark:text-white" />
              <div className="h-8 w-px bg-slate-300 dark:bg-slate-700" />
              <SalamaLogo className="h-8 w-auto" />
            </div>

            <div className="flex items-center gap-4">
              <ThemeToggle className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => openAuthModal("login")}
                className="cursor-pointer gap-1.5"
              >
                Login
              </Button>
            </div>
          </div>
        </header>
      )}

      <main className="flex-1 px-6 py-8">
        <div className="mx-auto max-w-5xl space-y-6">
          {/* Top action bar: Edit User Info + Sort */}
          {!isPremiumSummaryStep && (
            <div className="flex items-center justify-between">
              <Link
                href={ROUTES.userInfo(locale)}
                className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-nova-400 hover:text-nova-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-nova-400"
              >
                <ChevronLeft size={15} />
                Edit User Info
              </Link>
              <PlanFilters
                lifeCover={lifeCover}
                onLifeCoverChange={setLifeCover}
                policyTerm={policyTerm}
                onPolicyTermChange={setPolicyTerm}
                paymentFrequency={paymentFrequency}
                onPaymentFrequencyChange={setPaymentFrequency}
                planType={planType}
                onPlanTypeChange={setPlanType}
                sortBy={sortBy}
                onSortChange={setSortBy}
                onClearFilters={handleClearFilters}
                activeFilterCount={activeFilterCount}
              />
            </div>
          )}

          {isSMEQuote && !isInsuredWithSalama && (
            <SMECategoryTabs
              categories={smeCategories}
              activeIndex={activeCategoryIndex}
              onSelect={(index) => {
                if (index === smeCategories.length && !tokens?.access_token) {
                  openAuthModal("login", "quote")
                  return
                }
                setActiveCategoryIndex(index)
              }}
              selectedPlans={selectedPlanPerCategory}
              isAuthenticated={!!tokens?.access_token}
              showHint={selectedCategoriesCount > 0 && selectedCategoriesCount === smeCategories.length - 1 && !hintDismissed}
              onDismissHint={() => setHintDismissed(true)}
            />
          )}

          {isInsuredWithSalama ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center dark:border-amber-800 dark:bg-amber-900/20">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
                <svg
                  className="h-8 w-8 text-amber-600 dark:text-amber-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-lg font-bold text-amber-800 dark:text-amber-300">
                Please Contact Our Team
              </h3>
              <p className="mb-4 text-sm text-amber-700 dark:text-amber-400">
                As you are currently insured with Salama, please reach out to
                Nova&apos;s team for a personalized quote
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link
                  href={ROUTES.userInfo(locale)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  <ChevronLeft size={16} />
                  Edit User Info
                </Link>
              </div>
            </div>
          ) : (
            <>
              {isPremiumSummaryStep && (
                <div className="mb-4 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 cursor-pointer"
                    onClick={() => {
                      resetQuotes()
                      router.push(ROUTES.userInfo(locale))
                    }}
                  >
                    Generate New Quote
                  </Button>
                </div>
              )}

              {isPremiumSummaryStep ? (
                <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
                  {/* Header row */}
                  <div className="mb-1 flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                        Premium Summary
                      </h2>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Review your plan selection for each employee category.
                      </p>
                    </div>
                  </div>

                  {summaryLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin text-nova-600" />
                    </div>
                  ) : (
                    <>
                      <div className="mt-5 divide-y divide-slate-100 dark:divide-slate-700">
                        {(contactedSummary?.groups ?? []).map((group, i) => (
                          <div
                            key={group.benefit_category}
                            className="flex items-center justify-between py-3"
                          >
                            <div className="flex items-center gap-3">
                              <span className="rounded-full bg-nova-600 px-2.5 py-0.5 text-xs font-bold text-white">
                                {
                                  smeCategories.find(
                                    (c) => c.key === group.benefit_category
                                  )?.label
                                }
                              </span>
                              <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                  {group.planName}
                                </p>
                                <p className="text-[10px] text-slate-400">
                                  {group.productName} {group.planTierName}
                                </p>
                              </div>
                            </div>
                            <span className="text-sm font-bold text-slate-900 dark:text-white">
                              {group.totalWithVat.toLocaleString()}{" "}
                              {group?.currency}
                              <span className="ml-1 text-[10px] font-normal text-slate-400">
                                /yr
                              </span>
                            </span>
                          </div>
                        ))}
                        {/* Pending categories not yet selected */}
                        {smeCategories
                          .filter(
                            (cat) =>
                              !(
                                contactedSummary?.categoriesSelected ?? []
                              ).includes(cat.key)
                          )
                          .map((cat) => (
                            <div
                              key={cat.key}
                              className="flex items-center justify-between py-3"
                            >
                              <div className="flex items-center gap-3">
                                <span className="rounded-full bg-slate-300 px-2.5 py-0.5 text-xs font-bold text-white dark:bg-slate-600">
                                  {cat.label}
                                </span>
                                <span className="text-sm text-slate-400 italic">
                                  No plan selected
                                </span>
                              </div>
                              <button
                                className="text-xs font-medium text-nova-600 hover:text-nova-700 dark:text-nova-400"
                                onClick={() => {
                                  const idx = smeCategories.findIndex(
                                    (c) => c.key === cat.key
                                  )
                                  setActiveCategoryIndex(idx)
                                }}
                              >
                                Select Plan
                              </button>
                            </div>
                          ))}
                      </div>

                      {/* Total row */}
                      <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3 dark:bg-slate-800">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Total Annual Premium
                        </span>
                        <span className="text-base font-bold text-slate-900 dark:text-white">
                          {(
                            contactedSummary?.totalWithVat ??
                            (contactedSummary?.groups ?? []).reduce(
                              (s, g) => s + g.totalWithVat,
                              0
                            )
                          ).toLocaleString()}{" "}
                          {contactedSummary?.currency ?? "AED"}
                        </span>
                      </div>

                      {/* Actions */}
                      {contactedSummary?.isComplete && (
                        <div className="mt-5 flex items-center gap-3">
                          {!mergeResult && (
                            <>
                              <button
                                onClick={() => {
                                  if (!allCategoriesSelected || isMerging)
                                    return
                                  setShowQuoteConfirmModal(true)
                                }}
                                disabled={!allCategoriesSelected || isMerging}
                                className="cursor-pointer rounded-full bg-nova-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-nova-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {isMerging ? (
                                  <span className="flex items-center gap-2">
                                    <Loader2
                                      size={14}
                                      className="animate-spin"
                                    />
                                    Generating…
                                  </span>
                                ) : (
                                  "Generate Quote"
                                )}
                              </button>
                              <Button
                                variant="link"
                                className="cursor-pointer text-red-500 hover:text-red-600"
                                onClick={() => setShowResetDialog(true)}
                              >
                                Reset
                              </Button>
                              {!allCategoriesSelected && (
                                <p className="text-xs text-slate-400">
                                  Select a plan for each category first
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                /* Plan List — category selection step */
                <div className="space-y-4">
                  {plansLoading ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16">
                      <Loader2 className="h-8 w-8 animate-spin text-nova-600" />
                      <p className="text-body-sm text-slate-500 dark:text-slate-400">
                        Fetching plans…
                      </p>
                    </div>
                  ) : plansError ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
                      {plansError}
                    </div>
                  ) : filteredPlans.length === 0 ? (
                    <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900">
                      No plans available
                    </div>
                  ) : null}
                  {!plansLoading &&
                    !plansError &&
                    filteredPlans.map((plan, planIdx) => {
                      const catKey =
                        smeCategories[activeCategoryIndex]?.key ?? ""
                      const isSelectedForCat = isSMEQuote
                        ? selectedPlanPerCategory[catKey] === plan.planTierId
                        : plan.slug === selectedPlanSlug
                      // Show category badge for the CURRENT active category when selected
                      const catLabel =
                        isSMEQuote && isSelectedForCat
                          ? smeCategories[activeCategoryIndex]?.label
                          : undefined
                      return (
                        <PlanComparisonCard
                          key={plan.slug ?? `plan-${planIdx}`}
                          plan={plan}
                          isHighlighted={isSelectedForCat}
                          paymentFrequency="yearly"
                          categoryBadge={catLabel}
                          memberCount={memberCount}
                          onBuyClick={
                            isSMEQuote
                              ? () => handleSelectPlanForCategory(plan.slug)
                              : undefined
                          }
                          isSMEQuote={isSMEQuote}
                        />
                      )
                    })}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <CompareTray />
      <CompareToast />
      <AuthModal />

      {/* Reset confirmation dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset plan selection?</DialogTitle>
            <DialogDescription>
              This will clear all selected plans across every category. You will
              be taken back to the plan list to start over.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowResetDialog(false)}
              disabled={isResetting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isResetting}
              onClick={async () => {
                const ok = await resetQuotes()
                if (ok) {
                  lastFetchedCatKeyRef.current = null
                  setActiveCategoryIndex(0)
                  setShowResetDialog(false)
                }
              }}
            >
              {isResetting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Resetting...
                </>
              ) : (
                "OK, Reset"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {showQuoteConfirmModal && contactedSummary?.groups && (
        <QuoteConfirmModal
          plan={
            {
              name: contactedSummary.groups[0]?.planName ?? "SME Corporate",
              slug: contactedSummary.groups[0]?.quoteId ?? "sme-multi",
              productName:
                contactedSummary.groups[0]?.productName ?? "SME Corporate",
            } as any
          }
          configValues={{}}
          smeSelections={contactedSummary.groups.map((g) => ({
            categoryLabel: `Category ${g.benefit_category}`,
            planSlug: g.quoteId,
            planName: `${g.productName} ${g.planTierName ?? g.planTier?.name ?? g.planName ?? ""}`.trim(),
            yearlyPrice: g.totalWithVat,
            currency: g.currency,
          }))}
          rootcurrency={{
            currency: contactedSummary?.currency,
          }}
          onClose={() => setShowQuoteConfirmModal(false)}
          onConfirm={() => setShowQuoteConfirmModal(false)}
          onSubmitOverride={async () => {
            const result = await mergeContacted()
            if (result) {
              // Redirect to the specifically generated merged quote detail page
              router.push(ROUTES.quoteDetail(locale, result.mergedQuoteId))

              // Clear visitor session state without deleting the cookie
              useVisitorQuotesStore.setState({
                mergeResult: null,
                contactedSummary: null,
              })

              return {
                id: result.mergedQuoteId,
                premium_total: result.total.totalWithVat,
                status: "generated",
                documents: [
                  {
                    type: "policy_doc",
                    file_url: `${process.env.NEXT_PUBLIC_S3_URL || ""}${result.pdfKey}`,
                  },
                ],
              } as any
            }
            throw new Error("Failed to generate quote")
          }}
        />
      )}
    </div>
  )
}
